import { RolodexEntryType } from 'types'
import { z } from 'zod'

/**
 * Runtime shape for Rolodex system-document load responses.
 * looseObject keeps unknown Rolodex fields so patches/metadata updates stay intact.
 */
// Older onboardings miss (or null out) metadata fields; a bad field must not
// drop the whole entry, or its document becomes unresolvable everywhere
// downstream.
export const SystemDocumentJSONSchema = z.looseObject({
  id: z.string(),
  document_id: z.string().optional().catch(undefined),
  source_document_id: z.string().optional().catch(undefined),
  pipeline_config_hash: z.string().optional().catch(undefined),
  size: z.coerce.number().catch(0),
  type: z.string().catch(''),
  uploadDate: z.string().catch(''),
  title: z.string().catch(''),
  filename: z.string().catch(''),
  description: z.string().catch(''),
  vendor_name: z.string().optional().catch(undefined),
  document_type: z.string().optional().catch(undefined),
  document_title: z.string().optional().catch(undefined),
  drawing_number: z.string().optional().catch(undefined),
  system: z.string().optional().catch(undefined),
  revision_code: z.string().optional().catch(undefined),
  revision_date: z.string().optional().catch(undefined),
  total_pages: z.coerce.number().optional().catch(undefined),
  rich_text_summary: z.string().optional().catch(undefined),
})

export const SystemDocumentEntrySchema = z.looseObject({
  entry: z.looseObject({
    id: z.string(),
    type: z.literal(RolodexEntryType.SYSTEM_DOCUMENT),
    data: SystemDocumentJSONSchema,
    // Rolodex tags are Partial<Record<RolodexEntryType, string | null>>.
    tags: z.record(z.string(), z.string().nullable()),
    updated_at: z.string(),
    last_updated_by: z.string().optional(),
  }),
  links: z.record(z.string(), z.unknown()).optional(),
})

export type SystemDocumentJSON = z.infer<typeof SystemDocumentJSONSchema>
export type SystemDocumentEntry = z.infer<typeof SystemDocumentEntrySchema>

export function isSystemDocumentEntry(
  value: unknown
): value is SystemDocumentEntry {
  return SystemDocumentEntrySchema.safeParse(value).success
}

/** Validate a Rolodex /entries/load payload as system-document entries. */
export function parseSystemDocumentEntries(
  data: unknown
): SystemDocumentEntry[] {
  if (!Array.isArray(data)) {
    console.error(
      'Invalid system-document Rolodex response: expected an array',
      data
    )
    return []
  }

  const parsed: SystemDocumentEntry[] = []
  for (const item of data) {
    const result = SystemDocumentEntrySchema.safeParse(item)
    if (result.success) {
      parsed.push(result.data)
      continue
    }
    console.error('Invalid system-document Rolodex entry shape', result.error)
  }
  return parsed
}

export function parseSystemDocumentEntry(
  data: unknown
): SystemDocumentEntry | undefined {
  const result = SystemDocumentEntrySchema.safeParse(data)
  if (result.success) {
    return result.data
  }
  console.error('Invalid system-document Rolodex entry shape', result.error)
  return undefined
}
